import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { banners, comics, categories } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';

export default function Home() {
  const [currentBanner, setCurrentBanner] = useState(0);

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [nextBanner]);

  const hotComics = comics.filter((c) => c.isHot).slice(0, 8);
  const newComics = comics.filter((c) => c.isNew).slice(0, 8);
  const latestChapters = [...comics]
    .sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime())
    .slice(0, 10);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '073漫画网',
    url: 'https://073mh.com',
    description: '073漫画网是免费看韩国漫画的便捷平台，蜜桃少年等作品已为您精心准备。提供海量韩国漫画免费在线阅读，每日更新最新热门漫画。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://073mh.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '073漫画网',
    url: 'https://073mh.com',
    description: '免费韩国漫画阅读平台',
    sameAs: ['https://twitter.com/073mh', 'https://weibo.com/073mh'],
  };

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '蜜桃少年漫画-免费阅读看韩国漫画-073漫画网',
    description: '073漫画网是免费看韩国漫画的便捷平台，蜜桃少年等作品已为您精心准备。',
    isPartOf: { '@type': 'WebSite', name: '073漫画网', url: 'https://073mh.com' },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://073mh.com',
      }],
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(homeSchema)}</script>

      <Navbar />

      <main>
        {/* Hero Banner */}
        <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
          {banners.map((banner, index) => (
            <Link
              key={index}
              to={banner.link}
              className={`absolute inset-0 transition-opacity duration-700 ${index === currentBanner ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
            >
              <img
                src={banner.image}
                alt={banner.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 z-20">
                <span className="inline-block bg-accent-500 text-background-50 text-xs px-3 py-1 rounded-full mb-4 whitespace-nowrap">
                  {banner.tag}
                </span>
                <h2 className="text-3xl md:text-5xl font-bold text-background-50 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                  {banner.title}
                </h2>
                <p className="text-background-50/80 text-sm md:text-base max-w-xl">
                  {banner.subtitle}
                </p>
              </div>
            </Link>
          ))}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-30">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBanner(index)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${index === currentBanner ? 'bg-background-50 w-6' : 'bg-background-50/40'}`}
                aria-label={`切换到第${index + 1}张Banner`}
              />
            ))}
          </div>
        </section>

        {/* Category Quick Nav */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 -mt-8 relative z-20">
          <div className="bg-background-50 rounded-xl p-5 grid grid-cols-4 md:grid-cols-8 gap-3">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-2 group cursor-pointer"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary-100 flex items-center justify-center group-hover:bg-primary-500 transition-colors">
                  <i className={`${cat.icon} text-primary-500 group-hover:text-background-50 text-xl transition-colors`}></i>
                </div>
                <span className="text-xs text-foreground-700 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* Hot Recommendations */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 mt-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              热门推荐
            </h2>
            <Link to="/category/romance" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-1">
              查看更多 <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {hotComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </section>

        {/* Latest Updates */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 mt-14">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              最新上架
            </h2>
            <Link to="/category/bl" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap flex items-center gap-1">
              查看更多 <i className="ri-arrow-right-line text-xs"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {newComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </section>

        {/* Recent Updates List */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 mt-14 mb-14">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            最近更新
          </h2>
          <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
            <div className="divide-y divide-background-200/50">
              {latestChapters.map((comic) => {
                const latest = comic.chapters[comic.chapters.length - 1];
                return (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.slug}`}
                    className="flex items-center gap-4 px-5 py-3.5 hover:bg-background-100 transition-colors cursor-pointer group"
                  >
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      className="w-10 h-14 rounded object-cover object-top flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors truncate whitespace-nowrap">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5 truncate whitespace-nowrap">
                        {latest.title}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{latest.updateDate}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-md whitespace-nowrap ${comic.status === '已完结' ? 'bg-background-200/70 text-foreground-500' : 'bg-secondary-100 text-secondary-900'}`}>
                        {comic.status}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}